<template>
  <page-header title="Programs">
    <a :href="`${apiDomain}/api/program/export`" class="btn-outline-primary ml-auto h-10 mr-4" download="appData.json" @click="exportData">
      <DocumentArrowDownIcon class="size-5 -ml-1 mr-2" />
      Export
    </a>

    <button
      v-if="isAdmin()"
      class="btn-outline-primary h-10 mr-4"
      :disabled="isUpdatingVersions"
      @click="updateVersions"
    >
      <ArrowPathIcon :class="['size-5 -ml-1 mr-2', { 'animate-spin': isUpdatingVersions }]" />
      {{ isUpdatingVersions ? 'Updating...' : 'Update versions' }}
    </button>

    <router-link
      to="/program"
      class="btn-primary ml-auto h-10"
    >
      <PlusCircleIcon class="size-5 -ml-1 mr-2" />
      Add
    </router-link>
  </page-header>

  <input
    ref="fileInputRef"
    type="file"
    accept=".csv"
    style="display: none"
    @change="handleFileSelect"
  >

  <grid-items-empty v-if="isEmpty && search === null" link="/program" />

  <template v-else>
    <div class="flex justify-between mb-4">
      <input-search v-model="search" class="max-w-lg grow" />

      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getPrograms"
      :columns="columns"
      :row-class="rowClass"
      :per-page="15"
      @delete="onDelete"
      @show-program="onShowProgram"
      @hide-program="onHideProgram"
      @enable="onEnable"
      @disable="onDisable"
    />
  </template>

  <summary-update-program-version
    :show="showResultsModal"
    :results="updateResults"
    @close="closeResultsModal"
  />
</template>

<script setup>
import { ref, markRaw } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import GridItemsEmpty from '../components/grid/GridItemsEmpty.vue';
import ProgramListActions from '../components/ProgramListActions.vue';
import ProgramIcon from '../components/ProgramIcon.vue';
import Grid from '../components/grid/Grid.vue';
import SummaryUpdateProgramVersion from '../components/SummaryUpdateProgramVersion.vue';
import { PlusCircleIcon, DocumentArrowDownIcon, ArrowPathIcon } from '@heroicons/vue/24/outline';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { useRouter } from 'vue-router';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import { isAdmin } from '../composables/useAuth';

const gridRef = ref(null);
const fileInputRef = ref(null);
const isEmpty = ref(false);
const isUpdatingVersions = ref(false);
const selectedFile = ref(null);
const showResultsModal = ref(false);
const updateResults = ref(null);
const router = useRouter();
const toast = useToast();

function rowClass(_row, index) {
  return (index % 2 ? 'bg-zinc-300/10' : '') + (_row.deletedAt || !_row.isEnabled ? ' [&>td:not(:last-child)]:!opacity-30' : ''); // stripes class for row
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const search = useRouteQuery('search', null);

watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function getUrl({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase(), search: search.value || '' };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/program/?${searchParams}`;
}

async function getPrograms(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = !data.value.total;

  return {
    total: data.value.total,
    rows: data.value.programs
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    titleClass: '', // class for title cell
    sortable: true
  },
  {
    title: 'Name',
    prop: 'name',
    component: markRaw(ProgramIcon),
    class: 'py-0!',
    titleClass: 'pl-14!',
    click: row => (isAdmin() ? router.push(`/program/${row.id}`) : router.push(`/program/seo/${row.id}`)),
    sortable: true
  },
  {
    title: 'Category',
    value: row => row.Category.name
  },
  {
    title: 'Developer',
    value: row => row.Developer.name
  },
  {
    title: 'Publisher',
    value: row => row.Publisher.name
  },
  {
    title: 'Type',
    prop: 'licenseType'
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(ProgramListActions)
  }
];

async function onDelete(row) {
  const confirm = await Modal.confirm({
    title: 'Delete Program',
    body: `Delete the program "${row.name}"?`,
    okButton: 'Delete',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/program/${row.id}`).delete().json();

  if (data.value?.success) {
    toast.success('Program deleted successfully');
    reloadGrid();
    return;
  }

  toast.error('Error deleting program');
}

async function onShowProgram(row) {
  const { data } = await useFetch(`${apiDomain}/api/program/show/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Program show successfully');
    reloadGrid();
    return;
  }

  toast.error('Error showing program');
}

async function onHideProgram(row) {
  const { data } = await useFetch(`${apiDomain}/api/program/hide/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Program hidden successfully');
    reloadGrid();
    return;
  }

  toast.error('Error hidden program');
}

async function onEnable(row) {
  const { data } = await useFetch(`${apiDomain}/api/program/enable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Program enabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error enabling program');
}

async function onDisable(row) {
  const { data } = await useFetch(`${apiDomain}/api/program/disable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Program disabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error disabling program');
}

function updateVersions() {
  // Trigger file input click
  fileInputRef.value?.click();
}

function showUpdateResultsModal(results) {
  updateResults.value = results;
  showResultsModal.value = true;
}

function closeResultsModal() {
  showResultsModal.value = false;
  updateResults.value = null;
}

function handleFileSelect(event) {
  const file = event.target.files[0];
  if (!file) return;
  selectedFile.value = file;
  toast.info(`Selected file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
  uploadVersionsFile(file);
  event.target.value = '';
}

async function uploadVersionsFile(file) {
  if (isUpdatingVersions.value) return;

  isUpdatingVersions.value = true;

  try {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await useFetch(`${apiDomain}/api/program/bulk-update-versions`)
      .post(formData)
      .json();

    if (data.value?.success) {
      // Show results modal
      showUpdateResultsModal(data.value);
      reloadGrid();
    } else {
      toast.error(data.value?.message || 'Error updating program versions');
    }
  } catch (error) {
    console.error('Upload error:', error);
    toast.error('Error updating program versions');
  } finally {
    isUpdatingVersions.value = false;
    selectedFile.value = null;
  }
}
</script>
